import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '@quorum/api';
import { AssociateQueryParameters, LocationQueryParameters, VehicleQueryParameters } from '@quorum/models/xs-query';
import {
  Address,
  Associate,
  AssociateReport,
  Location,
  MessageType,
  Status,
  SubType,
  Vehicle,
  VehicleBody,
} from '@quorum/models/xs-resource';
import { createFormGroupFromObject } from 'libs/sha-services/src/lib/createFormGroupFromObject';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import { OemIntegrationService } from '@quorum/quorum-api';
import { HttpHeaders } from '@angular/common/http';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { google } from 'google-maps';

@Component({
  selector: 'sha-create-vehicle-with-associate',
  templateUrl: './create-vehicle-with-associate.component.html',
  styleUrls: ['./create-vehicle-with-associate.component.scss'],
})
export class CreateVehicleWithAssociateComponent implements OnInit, OnChanges, OnDestroy {
  @Input() associate: Associate;
  @Input() address: Address;
  @Input() vehicle: Vehicle;
  @Input() vehicleDropdowns$: Observable<any>;
  @Input() associateDropdowns$: Observable<any>;
  @Input() addressDropdowns$: Observable<any>;
  @Output() createEmitted: EventEmitter<{
    vehicle: Vehicle;
    associate: Associate;
    address: Address;
    vehicleQueryParams: any;
  }> = new EventEmitter<{ vehicle: Vehicle; associate: Associate; address: Address; vehicleQueryParams: any }>();
  associateForm: FormGroup;
  addressForm: FormGroup;
  vehicleForm: FormGroup;
  filteredAssociates: AssociateReport[] = [];
  selectedStepIndex: number = 0;
  trims: any[] = [];
  createReady: boolean = false;
  searchedAssociateName: string = '';
  dateMask = createAutoCorrectedDatePipe('mm/dd/yyyy');
  minInServiceDate: Date = new Date();
  maxInServiceDate: Date = new Date(new Date().setFullYear(new Date().getFullYear() + 5));
  locations$: Subscription;
  selectedAssoc: AssociateReport;
  associateFormChangesSub: Subscription;
  addressFormChangesSub: Subscription;
  associateFormNameSub: Subscription;
  vehicleFormVinSub: Subscription;
  vehicleFormTrimLevelSub: Subscription;
  addressFormProvStateSub: Subscription;
  vinError: string = '';
  phoneNumberMask = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
  filteredLocations$: Observable<Location[]>;
  bodyDropdowns$: Observable<VehicleBody[]>;
  vehicleFormChangesSub: Subscription;
  associateFormCombinedSub: Subscription;
  addressLineSub: Subscription;
  addressPredictions: any[];
  placesService: any;
  hyundaiVinDetailsData: any;
  gmVinVehicleInfo: any;
  vehicleQueryParams = { disableTranslation: false };

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private oemIntegrationService: OemIntegrationService,
    private authenticationStateService: AuthenticationStateService
  ) {}

  ngOnInit() {
    this.associateFormNameSub = this.associateForm['controls'].name.valueChanges
      .pipe(
        filter((input: string) => input.length >= 3),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((value: any) => {
          let params: AssociateQueryParameters = new AssociateQueryParameters({
            search: value,
          });
          this.searchedAssociateName = value;
          return this.apiService.get<AssociateReport[]>('v/1/reporting/associates', { params: params }).pipe(
            map((associates) => {
              this.filteredAssociates = associates;
              this.associateForm
                .get('firstName')
                .setValue(
                  this.associateForm.get('name').value.split(',')[1]
                    ? this.associateForm.get('name').value.split(',')[1].trim()
                    : this.associateForm.get('name').value
                );
              this.associateForm
                .get('lastName')
                .setValue(
                  this.associateForm.get('name').value.split(',')[1]
                    ? this.associateForm.get('name').value.split(',')[0].trim()
                    : null
                );
            }),
            catchError((error) => {
              this.associateForm.enable({ emitEvent: false });
              return of(error);
            })
          );
        })
      )
      .subscribe();

    this.associateFormCombinedSub = combineLatest(
      this.associateDropdowns$,
      this.associateForm.get('messagePreferenceId').valueChanges
    )
      .pipe(
        map(([appointmentDropdowns, messagePreference]) => {
          return { messageTypes: appointmentDropdowns.messageTypes, messagePreference };
        })
      )
      .subscribe((combinedData) => {
        if (
          combinedData.messageTypes
            .find((messageType: MessageType) => messageType.id === combinedData.messagePreference)
            .description.toLowerCase() === 'email'
        ) {
          this.associateForm.get('emailAddress').setValidators([Validators.email, Validators.required]);
          this.associateForm.get('emailAddress').markAsTouched();
          this.associateForm.get('emailAddress').updateValueAndValidity();
          this.addressForm.get('cellPhoneNumber').clearValidators();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
        } else if (
          combinedData.messageTypes
            .find((messageType: MessageType) => messageType.id === combinedData.messagePreference)
            .description.toLowerCase() === 'text'
        ) {
          this.addressForm.get('cellPhoneNumber').setValidators(Validators.required);
          this.addressForm.get('cellPhoneNumber').markAsTouched();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
          this.associateForm.get('emailAddress').setValidators([Validators.email]);
          this.associateForm.get('emailAddress').updateValueAndValidity();
        } else {
          this.associateForm.get('emailAddress').setValidators([Validators.email]);
          this.associateForm.get('emailAddress').updateValueAndValidity();
          this.addressForm.get('cellPhoneNumber').clearValidators();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
        }
      });

    this.associateFormChangesSub = this.associateForm.valueChanges.subscribe((value) => {
      if (
        (this.associateForm.valid || this.associateForm.disabled) &&
        (this.addressForm.valid || this.addressForm.disabled) &&
        (this.vehicleForm.valid || this.vehicleForm.disabled)
      ) {
        this.createReady = true;
      } else {
        this.createReady = false;
      }
    });

    this.addressFormChangesSub = this.addressForm.valueChanges.subscribe((value) => {
      if (
        (this.associateForm.valid || this.associateForm.disabled) &&
        (this.addressForm.valid || this.addressForm.disabled) &&
        (this.vehicleForm.valid || this.vehicleForm.disabled)
      ) {
        this.createReady = true;
      } else {
        this.createReady = false;
      }
    });

    this.vehicleFormChangesSub = this.vehicleForm.valueChanges.subscribe((value) => {
      if (
        (this.associateForm.valid || this.associateForm.disabled) &&
        (this.addressForm.valid || this.addressForm.disabled) &&
        (this.vehicleForm.valid || this.vehicleForm.disabled)
      ) {
        this.createReady = true;
      } else {
        this.createReady = false;
      }
    });

    this.vehicleFormVinSub = this.vehicleForm['controls'].vin.valueChanges
      .pipe(
        filter((input: string) => input.length >= 10),
        debounceTime(500),
        tap((vin) => {
          this.hyundaiVinDetailsData = null;
          this.trims = [];
          this.vinError = '';
          this.vehicleQueryParams.disableTranslation = false;
          if (vin.length < 17) {
            this.vinError = 'vinLengthFailed';
          } else {
            let vinTotal: number = 0;
            let digitValue: number;
            let checkDigit: any;
            for (var i = 0; i < vin.length; i++) {
              if (i !== 8) {
                switch (vin.charAt(i)) {
                  case 'A':
                  case 'J':
                    digitValue = 1;
                    break;
                  case 'B':
                  case 'K':
                  case 'S':
                    digitValue = 2;
                    break;
                  case 'C':
                  case 'L':
                  case 'T':
                    digitValue = 3;
                    break;
                  case 'D':
                  case 'M':
                  case 'U':
                    digitValue = 4;
                    break;
                  case 'E':
                  case 'N':
                  case 'V':
                    digitValue = 5;
                    break;
                  case 'F':
                  case 'O':
                  case 'W':
                    digitValue = 6;
                    break;
                  case 'G':
                  case 'P':
                  case 'X':
                    digitValue = 7;
                    break;
                  case 'H':
                  case 'Y':
                    digitValue = 8;
                    break;
                  case 'R':
                  case 'Z':
                    digitValue = 9;
                    break;
                  default:
                    digitValue = Number(vin.charAt(i));
                    break;
                }
              } else {
                checkDigit = vin.charAt(i);
              }

              switch (i + 1) {
                case 1:
                case 11:
                  vinTotal += digitValue * 8;
                  break;
                case 2:
                case 12:
                  vinTotal += digitValue * 7;
                  break;
                case 3:
                case 13:
                  vinTotal += digitValue * 6;
                  break;
                case 4:
                case 14:
                  vinTotal += digitValue * 5;
                  break;
                case 5:
                case 15:
                  vinTotal += digitValue * 4;
                  break;
                case 6:
                case 16:
                  vinTotal += digitValue * 3;
                  break;
                case 7:
                case 17:
                  vinTotal += digitValue * 2;
                  break;
                case 8:
                  vinTotal += digitValue * 10;
                  break;
                case 9:
                  break;
                case 10:
                  vinTotal += digitValue * 9;
                  break;
              }
            }
            if (vinTotal % 11 == checkDigit || (vinTotal % 11 == 10 && checkDigit === 'X')) {
            } else {
              this.vinError = 'vinCheckDigitFailed';
            }
          }
        }),
        switchMap((vin: any) => {
          let vehicleQueryParams: VehicleQueryParameters = new VehicleQueryParameters({ vin: vin });
          return this.apiService.get<any[]>(`v/1/vehicles/vehicles`, { params: vehicleQueryParams }).pipe(
            mergeMap((vehicles) => {
              return of({ vin, vehicles });
            })
          );
        }),
        switchMap((data) => {
          if (data.vehicles.length > 0) {
            this.clearVehicleForm(['vin', 'rooftopId']);
            this.disableVehicleForm(['vin']);
            this.vehicleForm.get('vin').setErrors({ vehicleExists: true });
            return of(null);
          } else {
            return this.apiService.get<any[]>(`v/1/vehicles/vin-translate/${data.vin}/1/trims`).pipe(
              map((trims) => {
                this.vehicleForm.get('vin').setErrors(null);
                if (trims.length > 0) {
                  this.vehicleForm.get('trimLevel').setValidators(Validators.required);
                  this.vehicleForm.get('trimLevel').updateValueAndValidity({ emitEvent: false });
                  this.disableVehicleForm(['vin', 'trimLevel']);

                  /* get data on any trim to check if manufacturer is HYU or GM */
                  this.apiService
                    .get<any[]>(`v/1/vehicles/vin-translate/${data.vin}/1/trims/${trims[0].trim}`)
                    .pipe(
                      map((vinData) => {
                        /* if manufacturer is HYU - have the trims options to contain only 1 trim item for selection */
                        if (vinData.manufacturer == 'HYU' || vinData.manufacturer == 'GEN') {
                          let headers: HttpHeaders = new HttpHeaders();
                          headers = headers.append('client_api_key', 'C53jiaePs0XuCN1dwYn+TMrtmZkgZLyhODuI8H3OubI=');
                          headers = headers.append('client_app_id', 'f0dde5a9-3082-4ea9-b60a-1c7967808eff');

                          return this.oemIntegrationService
                            .get(`hyundai/vehicles/vin-details/en/HYN/${data.vin}`, {
                              headers: headers,
                            })
                            .pipe(
                              map((hyundaiVinDetails) => {
                                if (hyundaiVinDetails !== 'No vehicle found.') {
                                  if (hyundaiVinDetails.VehicleTrim !== '-') {
                                    this.trims = [
                                      { id: hyundaiVinDetails.VehicleTrimId, trim: hyundaiVinDetails.VehicleTrim },
                                    ];
                                  } else {
                                    /* use Carfax trims if no trim supplied by VINDetails */
                                    this.trims = trims;
                                  }
                                  /* store Hyundai vinDetails data to populate the form later on trim selection */
                                  this.hyundaiVinDetailsData = hyundaiVinDetails;
                                } else {
                                  /* if 'No Hyundai vehicle found' - use Carfax data to populate the form */
                                  this.trims = trims;
                                }
                                if (this.trims.length == 1) {
                                  this.vehicleForm.patchValue({
                                    trimLevel: this.trims[0].trim,
                                  });
                                }
                              }),
                              catchError((error) => {
                                /* if an error - use Carfax data to populate the form */
                                this.trims = trims;
                                return of(error);
                              })
                            )
                            .subscribe();
                        } else {
                          /* if manufacturer is not HYU - have vin-translate trims for dropdown selection */
                          this.trims = trims;
                        }
                      })
                    )
                    .subscribe();
                } else {
                  this.trims = [];
                  this.vehicleForm.get('trimLevel').patchValue(null, { emitEvent: false });
                  this.enableVehicleForm(['trimLevel']);
                }
                this.clearVehicleForm(['vin', 'rooftopId']);
              }),
              catchError((error) => {
                if (error.statusCode == 422) {
                  this.trims = [];
                  this.vehicleForm.get('trimLevel').patchValue(null, { emitEvent: false });
                  this.enableVehicleForm(['trimLevel']);
                  this.clearVehicleForm(['vin', 'rooftopId']);
                }
                return of(error);
              })
            );
          }
        })
      )
      .subscribe();

    this.vehicleFormTrimLevelSub = this.vehicleForm['controls'].trimLevel.valueChanges
      .pipe(
        debounceTime(500),
        switchMap((value: any) => {
          return combineLatest(
            this.apiService.get<any[]>(
              `v/1/vehicles/vin-translate/${this.vehicleForm.get('vin').value}/1/trims/${value}`
            ),
            this.vehicleDropdowns$
          ).pipe(
            switchMap((vinTranslate) => {
              this.bodyDropdowns$ = combineLatest(
                this.apiService.get<any[]>(`v/1/vehicles/vehicle-bodies`),
                this.vehicleForm.get('body').valueChanges.pipe(filter((value) => value.length > 1))
              ).pipe(
                map(([bodies, value]) => {
                  return bodies.filter((body: any) => body.vehicleBody.toLowerCase().includes(value.toLowerCase()));
                })
              );
              return of(vinTranslate);
            }),
            switchMap((data): any => {
              if (this.hyundaiVinDetailsData) {
                /* creating a HYU vehicle */
                this.enableVehicleForm();
                this.vehicleForm.patchValue({
                  ...this.vehicleForm,
                  year: this.hyundaiVinDetailsData.VehicleYear,
                  make: data[1].makes.find((make: any) => make.description.toLowerCase() === data[0].make.toLowerCase())
                    ? data[1].makes.find((make: any) => make.description.toLowerCase() === data[0].make.toLowerCase())
                        .description
                    : null,
                  model: this.hyundaiVinDetailsData.Carline,
                  engine: this.hyundaiVinDetailsData.Engine,
                  subTypeId: data[0].subTypeId ? Number(data[0].subTypeId) : null,
                  body: data[0].body,
                  manufacturerId: data[0].manufacturer,
                  transmission: this.hyundaiVinDetailsData.Transmission,
                  interiorColourDescription: this.hyundaiVinDetailsData.InteriorColour,
                  exteriorColour: this.hyundaiVinDetailsData.ExteriorColour,
                  hscCode: this.hyundaiVinDetailsData.HSC,
                  retailDealerNumber: this.hyundaiVinDetailsData.RetailDealer,
                  deliveryDate:
                    this.hyundaiVinDetailsData.PurchaseDate != '0001-01-01T00:00:00'
                      ? this.hyundaiVinDetailsData.PurchaseDate
                      : null,
                  regularWarrantyId: null,
                });
              } else if (this.gmVinVehicleInfo) {
                /* creating a GM vehicle */
                this.enableVehicleForm();
                this.vehicleForm.patchValue({
                  ...this.vehicleForm,
                  year: this.gmVinVehicleInfo.vehicleFileRecord.toYear,
                  make: data[1].makes.find(
                    (make: any) =>
                      make.description.toLowerCase() === this.gmVinVehicleInfo.vehicleFileRecord.manuCode.toLowerCase()
                  )
                    ? data[1].makes.find(
                        (make: any) =>
                          make.description.toLowerCase() ===
                          this.gmVinVehicleInfo.vehicleFileRecord.manuCode.toLowerCase()
                      ).description
                    : null,
                  model: this.gmVinVehicleInfo.vehicleFileRecord.modelDesc,
                  engine: this.gmVinVehicleInfo.vehicleFileRecord.engineID,
                  subTypeId: data[0].subTypeId ? Number(data[0].subTypeId) : null,
                  body: this.gmVinVehicleInfo.vehicleFileRecord.bodyDesc,
                  manufacturerId: data[0].manufacturer,
                  deliveryDate: this.gmVinVehicleInfo.ctVehicleList.ctVehicleAttributes
                    ? new Date(
                        this.gmVinVehicleInfo.ctVehicleList.ctVehicleAttributes.firstRegistrationDate
                      ).toISOString()
                    : null,
                  regularWarrantyId:
                    data[0].manufacturer.toLowerCase() === 'gm'
                      ? data[0].make.toLowerCase() === 'cadillac'
                        ? 129
                        : 1
                      : null,
                });
                this.vehicleQueryParams.disableTranslation = true;
              } else {
                this.enableVehicleForm();
                this.vehicleForm.patchValue({
                  ...this.vehicleForm,
                  year: data[0].year,
                  make: data[1].makes.find((make: any) => make.description.toLowerCase() === data[0].make.toLowerCase())
                    ? data[1].makes.find((make: any) => make.description.toLowerCase() === data[0].make.toLowerCase())
                        .description
                    : null,
                  model: data[0].model,
                  engine: data[0].engine,
                  subTypeId: data[0].subTypeId ? Number(data[0].subTypeId) : null,
                  body: data[0].body,
                  manufacturerId: data[0].manufacturer,
                  regularWarrantyId:
                    data[0].manufacturer.toLowerCase() === 'gm'
                      ? data[0].make.toLowerCase() === 'cadillac'
                        ? 129
                        : 1
                      : null,
                });
              }

              return this.apiService
                .get<Status[]>(`v/1/vehicles/vehicle-statuses`, {
                  params: { code: 4, manufacturerId: data[0].manufacturer },
                })
                .pipe(
                  map((statuses) => {
                    this.vehicleForm.patchValue({
                      ...this.vehicleForm,
                      statusCode: statuses[0].id,
                      subCategoryId: 2,
                    });
                  }),
                  catchError((error) => {
                    return of(error);
                  })
                );
            }),
            catchError((error) => {
              return of(error);
            })
          );
        })
      )
      .subscribe();

    this.addressFormProvStateSub = this.addressForm
      .get('provStateId')
      .valueChanges.pipe(
        tap((provState) => {
          this.addressForm.patchValue({
            locationId: '',
            countryId: provState.countryId,
          });
        }),
        switchMap((provState) => {
          let params: LocationQueryParameters = new LocationQueryParameters({
            provStateId: provState,
            pageSize: 2000,
          });
          return this.apiService.get<Location[]>('v/1/associates/locations', { params: params }).pipe(
            switchMap((locations) => {
              this.filteredLocations$ = this.addressForm.get('locationId').valueChanges.pipe(
                startWith(
                  this.selectedAssoc
                    ? locations.find((location: Location) => location.id === this.selectedAssoc.locationId)
                    : ''
                ),
                map((value) => {
                  if (typeof value === 'string') {
                    return locations.filter((option: any) =>
                      option.description.toLowerCase().includes(value.toLowerCase())
                    );
                  } else {
                    return locations.filter((option: any) => option.id === value.id);
                  }
                }),
                tap((locations: Location[]) => {
                  if (this.selectedAssoc) {
                    this.addressForm.patchValue(
                      { locationId: locations.find((loco) => loco.id === this.selectedAssoc.locationId) },
                      { emitEvent: false }
                    );
                  } else {
                    this.addressForm.get('locationId').enable({ emitEvent: false });
                  }
                })
              );
              return of(null);
            })
          );
        })
      )
      .subscribe((provState: any) => {});

    this.addressLineSub = this.addressForm
      .get('addressLine1')
      .valueChanges.pipe(
        filter((input: string) => {
          if (input) {
            return input.length >= 4;
          }
        }),
        debounceTime(500),
        distinctUntilChanged(),
        map((value) => {
          this.getAutocompletePredictions(value);
        })
      )
      .subscribe(() => {});
  }

  ngOnDestroy() {
    this.associateFormChangesSub.unsubscribe();
    this.addressFormChangesSub.unsubscribe();
    this.associateFormNameSub.unsubscribe();
    this.vehicleFormVinSub.unsubscribe();
    this.vehicleFormTrimLevelSub.unsubscribe();
    this.addressFormProvStateSub.unsubscribe();
    this.vehicleFormChangesSub.unsubscribe();
    this.associateFormCombinedSub.unsubscribe();
  }

  getAutocompletePredictions(searchedAddressValue: any) {
    let autocompleteService = new google.maps.places.AutocompleteService();

    autocompleteService.getPlacePredictions(
      {
        types: ['address'],
        input: searchedAddressValue,
        componentRestrictions: { country: ['ca', 'us'] },
      },
      (predictions: any, status: any) => {
        if (status == google.maps.places.PlacesServiceStatus.OK) {
          this.addressPredictions = predictions;
        } else {
          console.log(status, 'error in address predictions');
        }
      }
    );
  }

  selectAddress(selectedAddress: any, addressDropdowns: any) {
    let addressField = document.querySelector('#addressLine1') as HTMLInputElement;
    this.placesService = new google.maps.places.PlacesService(addressField);

    this.placesService.getDetails(
      { placeId: selectedAddress.place_id, fields: ['address_components'] },
      (result: any, status: any) => {
        let address: any = {
          addressLine1: '',
          provStateId: '',
          locationName: '',
          postalZipCode: '',
          countryId: '',
          dropdownsLocation: null,
          location: null,
        };

        for (const component of result.address_components) {
          const componentType = component.types[0];

          switch (componentType) {
            case 'country': {
              address.countryId = component.short_name === 'CA' ? 'CAN' : component.short_name === 'US' ? 'USA' : null;

              this.addressForm
                .get('countryId')
                .patchValue(component.short_name === 'CA' ? 'CAN' : component.short_name === 'US' ? 'USA' : null, {
                  emitEvent: false,
                });
              break;
            }

            case 'street_number': {
              address.addressLine1 = `${component.long_name} `;
              break;
            }

            case 'route': {
              address.addressLine1 += component.short_name;
              break;
            }

            case 'locality': {
              address.locationName = component.long_name;
              break;
            }

            case 'postal_code_prefix': {
              address.postalZipCode = component.long_name;
              break;
            }

            case 'postal_code': {
              address.postalZipCode = component.long_name;
              break;
            }

            case 'postal_code_suffix': {
              address.postalZipCode = `${address.postalZipCode}-${component.long_name}`;
              break;
            }

            case 'administrative_area_level_1': {
              this.addressForm.get('locationId').enable({ emitEvent: false });

              this.addressForm
                .get('provStateId')
                .patchValue(component.short_name === 'QC' ? 'PQ' : component.short_name, {
                  emitEvent: false,
                });

              address.provStateId = component.short_name === 'QC' ? 'PQ' : component.short_name;
              break;
            }
          }
        }

        if (address.locationName) {
          let selectedLocation: Location = addressDropdowns.locations.filter((option: Location): any => {
            return (
              option.description.toLowerCase().includes(address.locationName.toLowerCase()) &&
              option.provStateId.toLowerCase().includes(address.provStateId.toLowerCase())
            );
          });

          if (selectedLocation[0]) {
            this.addressForm.get('locationId').patchValue(selectedLocation[0], { emitEvent: false });
            this.addressForm.get('countryId').patchValue(selectedLocation[0].countryId, { emitEvent: false });
            address.dropdownsLocation = selectedLocation[0];
          } else {
            this.addressForm.get('locationId').patchValue(null);
          }
        }

        this.addressForm.get('addressLine1').patchValue(address.addressLine1, { emitEvent: false });
        this.addressForm.get('postalZipCode').patchValue(address.postalZipCode, { emitEvent: false });

        if (address.dropdownsLocation) {
          this.addressForm.get('locationId').disable();
        }

        this.addressPredictions = [];
      }
    );
  }

  disableVehicleForm(fieldsToSkip?: string[]) {
    if (fieldsToSkip && fieldsToSkip.length > 0) {
      Object.keys(this.vehicleForm.getRawValue()).forEach((key) => {
        if (!fieldsToSkip.includes(key)) {
          this.vehicleForm.get(key).disable({ emitEvent: false });
        } else {
          this.vehicleForm.get(key).enable({ emitEvent: false });
        }
      });
    } else {
      Object.keys(this.vehicleForm.getRawValue()).forEach((key) => {
        this.vehicleForm.get(key).disable({ emitEvent: false });
      });
    }
  }

  clearVehicleForm(fieldsToSkip?: string[]) {
    if (fieldsToSkip && fieldsToSkip.length > 0) {
      Object.keys(this.vehicleForm.getRawValue()).forEach((key) => {
        if (!fieldsToSkip.includes(key)) {
          this.vehicleForm.get(key).patchValue(null, { emitEvent: false });
        }
      });
    } else {
      Object.keys(this.vehicleForm.getRawValue()).forEach((key) => {
        this.vehicleForm.get(key).patchValue(null, { emitEvent: false });
      });
    }
  }

  disableAssociateForm() {
    Object.keys(this.associateForm.getRawValue()).forEach((key) => {
      if (key !== 'name') {
        this.associateForm.get(key).disable({ emitEvent: false });
      }
    });
  }
  disableAddressForm() {
    Object.keys(this.addressForm.getRawValue()).forEach((key) => {
      this.addressForm.get(key).disable({ emitEvent: false });
    });
  }

  enableVehicleForm(fieldsToSkip?: string[]) {
    if (fieldsToSkip && fieldsToSkip.length > 0) {
      Object.keys(this.vehicleForm.getRawValue()).forEach((key) => {
        if (!fieldsToSkip.includes(key)) {
          this.vehicleForm.get(key).enable({ emitEvent: false });
        } else {
          this.vehicleForm.get(key).disable({ emitEvent: false });
        }
      });
    } else {
      Object.keys(this.vehicleForm.getRawValue()).forEach((key) => {
        this.vehicleForm.get(key).enable({ emitEvent: false });
      });
    }
  }

  enableAssociateForm() {
    Object.keys(this.associateForm.getRawValue()).forEach((key) => {
      if (key !== 'name') {
        this.associateForm.get(key).enable({ emitEvent: false });
      }
    });
  }
  enableAddressForm() {
    Object.keys(this.addressForm.getRawValue()).forEach((key) => {
      this.addressForm.get(key).enable({ emitEvent: false });
    });
  }

  setAssociateRequiredFields() {
    this.associateForm.get('name').setValidators(Validators.required);
    this.associateForm.get('messagePreferenceId').setValidators(Validators.required);
    this.associateForm.get('emailAddress').setValidators(Validators.email);
  }

  setAddressRequiredFields() {
    this.addressForm.get('addressLine1').setValidators(Validators.required);
    this.addressForm.get('locationId').setValidators(Validators.required);
    this.addressForm.get('provStateId').setValidators(Validators.required);
    this.addressForm.get('locationId').disable({ emitEvent: false, onlySelf: true });
  }

  setVehicleRequiredFields() {
    this.vehicleForm.get('vin').setValidators(Validators.required);
    this.vehicleForm.get('subTypeId').setValidators(Validators.required);
    this.vehicleForm.get('year').setValidators(Validators.required);
    this.vehicleForm.get('make').setValidators(Validators.required);
    this.vehicleForm.get('model').setValidators(Validators.required);
    this.vehicleForm.get('body').setValidators(Validators.required);
    this.vehicleForm.get('engine').setValidators(Validators.required);
    this.vehicleForm.get('prospectColourId').setValidators(Validators.required);
  }

  createVehicle(associate: Associate, address: any, vehicle: Vehicle) {
    let updatedAddress: Address = {
      ...address,
      locationId: address.locationId.id ? address.locationId.id : address.locationId,
      cellPhoneNumber: address.cellPhoneNumber ? address.cellPhoneNumber.replace(/\D+/g, '') : null,
      homePhoneNumber: address.homePhoneNumber ? address.homePhoneNumber.replace(/\D+/g, '') : null,
    };
    this.createEmitted.emit({
      associate: associate,
      address: updatedAddress,
      vehicle: vehicle,
      vehicleQueryParams: this.vehicleQueryParams,
    });
    this.createReady = false;
  }

  setVehicleType(subType: SubType) {
    this.vehicleForm.patchValue({ typeId: subType.typeId });
  }

  setPostalCodeAndProvince(location: Location) {
    this.addressForm.patchValue({
      location: location,
      postalZipCode: location.postalCode,
      countryId: location.countryId,
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.vehicle) {
      this.vehicleForm = createFormGroupFromObject(changes.vehicle.currentValue);
      this.disableVehicleForm(['vin']);
      if (changes.vehicle.currentValue.vin != null) {
        this.vehicleForm.get('vin').setValue(changes.vehicle.currentValue.vin);
        this.apiService
          .get<any[]>(`v/1/vehicles/vin-translate/${changes.vehicle.currentValue.vin}/1/trims`)
          .pipe(
            map((trims) => {
              this.trims = trims;
            }),
            catchError((error) => {
              return of(error);
            }),
            take(1)
          )
          .subscribe();
      }
      this.setVehicleRequiredFields();
      this.vehicleForm.markAllAsTouched();
    }
    if (changes.associate) {
      this.associateForm = createFormGroupFromObject(changes.associate.currentValue);
      this.associateForm.addControl('name', new FormControl(''));
      this.setAssociateRequiredFields();
      this.associateForm.markAllAsTouched();
      this.disableAssociateForm();
    }
    if (changes.address) {
      this.addressForm = createFormGroupFromObject(changes.address.currentValue);
      this.setAddressRequiredFields();
      this.addressForm.markAllAsTouched();
      this.disableAddressForm();
    }
  }

  selectAssociate(value: any, filteredAssociates?: AssociateReport[]) {
    if (value == 'addAssociate') {
      this.selectedAssoc = null;
      this.associateForm.get('name').setValue(this.searchedAssociateName);
      this.enableAddressForm();
      this.enableAssociateForm();
    } else {
      if (filteredAssociates) {
        let selectedAssociate = filteredAssociates.find((associate) => associate.id == value);
        this.selectedAssoc = selectedAssociate;
        this.associateForm.patchValue(
          {
            ...this.associateForm,
            emailAddress: `${selectedAssociate.emailAddress}`,
            classId: selectedAssociate.classId,
            messagePreferenceId: selectedAssociate.messagePreferenceId,
          },
          { emitEvent: false }
        );
        this.disableAssociateForm();
        this.addressForm.patchValue({
          ...this.addressForm,
          addressLine1: selectedAssociate.addressLine1,
          homePhoneNumber: selectedAssociate.homePhoneNumber,
          cellPhoneNumber: selectedAssociate.cellPhoneNumber,
          countryId: selectedAssociate.countryId,
          postalZipCode: selectedAssociate.postalZipCode,
          provStateId: selectedAssociate.provStateId,
        });
        this.disableAddressForm();
        this.vehicleForm.patchValue({ associateId: value });
      }
    }

    this.filteredAssociates = [];
  }

  displayAssociateName(filteredAssociates: AssociateReport[], value: string) {
    let displayAssociate: AssociateReport;
    if (filteredAssociates.length > 0) {
      displayAssociate = filteredAssociates.find((associate: AssociateReport) => associate.id === value);
      if (displayAssociate) {
        return displayAssociate.firstName
          ? `${displayAssociate.lastName}, ${displayAssociate.firstName}`
          : `${displayAssociate.fullName}`;
      } else {
        return value;
      }
    } else {
      return value;
    }
  }

  displayLocationName(location: Location): string {
    return location && location.description
      ? location.description + ' ' + location.provStateId + ', ' + location.countryId
      : '';
  }

  makeUpperCase(str: string) {
    if (str) {
      str = str[0].toUpperCase() + str.slice(1);
      str = str.replace(/,\s*([a-z])/g, function (d, e) {
        return ', ' + e.toUpperCase();
      });
      str = str.replace(/'\s*([a-z])/g, function (d, e) {
        return "'" + e.toUpperCase();
      });
      return str;
    }
    return '';
  }
}
